<template>
    <div class="px-2 py-4">

        <div class="d-flex justify-space-between align-center flex-wrap sm-gap">
            <div class="font-weight-bold text-darkblue">Parcours</div>
            <div class="text-darkblue">{{ readable_courses }}</div>
        </div>
        <div v-if="get_vague_name" class="d-flex justify-space-between align-center flex-wrap sm-gap">
            <div class="font-weight-bold text-darkblue">Vague</div>
            <div class="text-darkblue">{{ get_vague_name }}</div>
        </div>

        <div v-if="show_equipe_selector" class="d-flex justify-space-between align-center flex-wrap sm-gap mt-2">
            <div class="font-weight-bold text-darkblue">Fais partie de l'équipe</div>
            <v-select :model-value="current_equipe_select_value" :items="equipe_select_items" item-title="title"
                item-value="value" :item-props="equipeItemProps" density="compact" hide-details class="equipe-select"
                @update:model-value="onEquipeChange" />
        </div>

        <v-divider class="mt-2"></v-divider>

        <div v-if="show_duplicate_inscription_block" class="mt-3 mb-2">
            <p class="text-body-2 text-darkblue mb-2">
                Vous souhaitez inscrire ce participant à un autre parcours, gagnez du temps en dupliquant ses
                informations.
            </p>
            <SportipsButton v-if="duplicate_disabled" icon="mdi-content-duplicate" class="cursor_lock" disabled
                mobile-full-width>
                Dupliquer
                <v-tooltip activator="parent" location="top">{{ duplicate_disabled_tooltip }}</v-tooltip>
            </SportipsButton>
            <SportipsButton v-else icon="mdi-content-duplicate" @onClick="duplicateInscription()" mobile-full-width>
                Dupliquer
            </SportipsButton>
        </div>

        <div class="d-flex justify-end sm-gap flex-wrap">
            <SportipsButton icon="mdi-account-group" v-if="show_equipe_selector" @onClick="scrollToGestionEquipes">
                Gérer l'équipe
            </SportipsButton>
            <SportipsButton icon="mdi-pencil" @onClick="$emit('editInscription')">Modifier</SportipsButton>

            <SportipsButton v-if="show_delete_button" icon="mdi-trash-can" @onClick="openDeleteDialog()">
                Supprimer
            </SportipsButton>
        </div>




    </div>
    <v-dialog width="500" max-width="100%" v-model="deleteDialog" scroll-strategy="none">
        <v-card>
            <v-card-text>
                <div class="text-body-1 text-center">
                    Etes-vous sûr de vouloir supprimer l'inscription de <br />
                    <b>{{ nom_coureur }}</b> ?
                </div>
                <div class="d-flex justify-center flex-wrap gap mt-2">
                    <v-btn color="dark-red" variant="text" @click="closeDeleteDialog()">Non</v-btn>
                    <v-btn color="dark-green" variant="text" @click="deleteInscription()">Oui</v-btn>
                </div>
            </v-card-text>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapStores } from 'pinia';
import { useMainStore } from '@/stores/mainStore';
import { useBookingSystemStore } from '@/stores/bookingSystemStore';

const CREATE_NEW_EQUIPE_VALUE = '__CREATE_NEW__';

export default {
    props: {
        inscription: {
            type: Object,
            required: true
        },
        index: {
            type: Number,
            required: true
        },
        noDelete: {
            type: Boolean,
            required: false,
            default: false
        }
    },
    data() {
        return {
            deleteDialog: false
        }
    },
    computed: {
        ...mapStores(useMainStore, useBookingSystemStore),
        show_duplicate_inscription_block() {
            if (!this.mainStore.module.allow_duplicate_inscription) return false;
            if (this.mainStore.billetterie_mode || this.mainStore.dossard_bourse_mode) return false;
            return this.has_selected_courses;
        },
        duplicate_disabled() {
            return this.bookingSystemStore.some_inscriptions_open
                || this.bookingSystemStore.limit_inscription_vip_reached
                || !this.bookingSystemStore.has_unique_equipes_names;
        },
        duplicate_disabled_tooltip() {
            if (this.bookingSystemStore.some_inscriptions_open) {
                return "Vous devez compléter l'inscription ouverte avant de dupliquer";
            }
            if (this.bookingSystemStore.limit_inscription_vip_reached) {
                return "Vous avez atteint la limite de participants de votre lien privilégié";
            }
            if (!this.bookingSystemStore.has_unique_equipes_names) {
                return "Vous devez donner un nom valide et différent à chacune de vos équipes";
            }
            return "";
        },
        nom_coureur() {
            return this.inscription.get_nom_coureur(this.mainStore.is_default_mode, this.index);
        },
        get_vague_name() {
            return this.inscription.get_vague_name();
        },
        has_selected_courses() {
            return this.inscription.get_courses().length > 0;
        },
        selected_courses() {
            return this.inscription.get_courses();
        },
        readable_courses() {
            if (!this.has_selected_courses) {
                return 'Inscription non sportive';
            }
            return this.selected_courses.map(course => course.nom).join(', ');
        },
        show_delete_button() {
            return !this.mainStore.dossard_bourse_mode && !this.noDelete;
        },
        show_equipe_selector() {
            if (!this.mainStore.module?.need_gestion_equipe) return false;
            return this.selected_courses.some((course) => course.is_equipe_course());
        },
        equipe_course_id() {
            const equipe_course = this.selected_courses.find((course) => course.is_equipe_course());
            return equipe_course?.id ?? null;
        },
        available_equipes() {
            if (!this.equipe_course_id || !this.mainStore.bookingSystem?.equipes?.list) return [];
            return this.mainStore.bookingSystem.equipes.list.filter(
                (equipe) => equipe.id_course == this.equipe_course_id
            );
        },
        current_equipe() {
            const equipesList = this.mainStore.bookingSystem?.equipes?.list ?? [];
            const equipeUid = this.inscription.coureur?.equipe_uid;
            if (equipeUid) {
                return equipesList.find(
                    (equipe) => equipe.uid == equipeUid && equipe.id_course == this.equipe_course_id
                ) ?? null;
            }
            return equipesList.find(
                (equipe) => equipe.id_course == this.equipe_course_id
                    && equipe.coureurs.some((coureur) => coureur.index == this.index)
            ) ?? null;
        },
        current_equipe_select_value() {
            const equipe = this.current_equipe;
            if (!equipe) return '';
            const listIndex = this.mainStore.bookingSystem.equipes.list.indexOf(equipe);
            return equipe.nom || `__index_${listIndex}__`;
        },
        equipe_select_items() {
            const equipesStore = this.mainStore.bookingSystem.equipes;
            const currentEquipe = this.current_equipe;
            const fullList = equipesStore.list;
            const items = this.available_equipes.map((equipe, index) => {
                const listIndex = fullList.indexOf(equipe);
                const size_max = equipe.size_max ?? equipe.size;
                const is_full = !equipesStore._equipe_has_available_spot(equipe);
                const is_current = equipe === currentEquipe;
                const label = equipe.nom || `Equipe n°${index + 1} (sans nom)`;
                const count = `${equipe.coureurs.length}/${size_max}`;
                const title = is_full && !is_current
                    ? `${label} (${count} - complète)`
                    : `${label} (${count})`;
                return {
                    title,
                    value: equipe.nom || `__index_${listIndex}__`,
                    disabled: is_full && !is_current,
                };
            });
            items.push({
                title: 'Créer une nouvelle équipe',
                value: CREATE_NEW_EQUIPE_VALUE,
                disabled: false,
            });
            return items;
        },
    },
    methods: {
        equipeItemProps(item) {
            return {
                disabled: item.disabled,
                class: item.disabled ? 'text-grey' : '',
            };
        },
        onEquipeChange(value) {
            if (!value || value === this.current_equipe_select_value) return;
            const equipesStore = this.mainStore.bookingSystem.equipes;
            if (value === CREATE_NEW_EQUIPE_VALUE) {
                equipesStore.moveCoureurToEquipe(this.index, null);
                return;
            }
            const indexMatch = value.match(/^__index_(\d+)__$/);
            if (indexMatch) {
                equipesStore.moveCoureurToEquipe(this.index, null, parseInt(indexMatch[1], 10));
                return;
            }
            equipesStore.moveCoureurToEquipe(this.index, value);
        },
        openDeleteDialog() {
            this.deleteDialog = true;
        },
        closeDeleteDialog() {
            this.deleteDialog = false;
        },
        deleteInscription() {
            this.$emit('deleteInscription');
            this.closeDeleteDialog();
        },
        scrollToGestionEquipes() {
            this.$nextTick(() => {
                const el = document.getElementById('gestion-equipes');
                if (!el) {
                    return;
                }
                const top = el.getBoundingClientRect().top + window.scrollY - 20;
                window.scrollTo({ top, behavior: 'smooth' });
            });
        },
        duplicateInscription() {
            if (this.duplicate_disabled) {
                return;
            }
            this.$emit('duplicateInscription');
        },
    }
}
</script>

<style lang="scss" scoped>
.equipe-select {
    max-width: 280px;
    min-width: 180px;
}
</style>